import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Archive, Loader2 } from "lucide-react";
import { Button, FormField, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { documentsApi } from "@/api/documents";
import { ApiError } from "@/api/client";
import type { DocumentDto } from "@/types/api";

interface ArchiveDocumentModalProps {
  document: DocumentDto | null;
  open: boolean;
  onClose: () => void;
  onArchived: (updated: DocumentDto) => void;
  /** Optional callback wired from the page so the success toast can flip
   * the "Show archived" toggle without leaking modal internals. */
  onShowArchivedRequested?: () => void;
}

const REASON_MAX = 500;

export function ArchiveDocumentModal({
  document,
  open,
  onClose,
  onArchived,
  onShowArchivedRequested,
}: ArchiveDocumentModalProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState("");

  // Reset when (re)opening, never on close — closing should not flicker
  // the textarea.
  useEffect(() => {
    if (open) setReason("");
  }, [open, document?.id]);

  const archive = useMutation({
    mutationFn: () =>
      documentsApi.archive(document!.id, reason.trim() || undefined),
    onSuccess: (updated) => {
      // Order matters: hand the row to the parent first so its cache
      // invalidation kicks off, THEN close the modal, THEN toast. The
      // toast is non-blocking so it doesn't visually replace the modal.
      onArchived(updated);
      onClose();
      toast({
        message: `“${updated.title}” has been archived.`,
        tone: "success",
        action: onShowArchivedRequested
          ? { label: "Show archived", onClick: onShowArchivedRequested }
          : undefined,
      });
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not archive the document.";
      toast({ message, tone: "error" });
    },
  });

  if (!document) return null;

  return (
    <Modal
      open={open}
      // Block close clicks while the request is in flight so the user
      // can't dismiss before the server responds and miss the result.
      onClose={() => {
        if (!archive.isPending) onClose();
      }}
      dismissOnBackdropClick={false}
      title="Archive Document"
      description="This document will be hidden from the active folder view. It can be restored at any time. The document and all its versions are retained."
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={archive.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => archive.mutate()}
            disabled={archive.isPending}
          >
            {archive.isPending ? (
              <Loader2 size={14} aria-hidden className="animate-spin" />
            ) : (
              <Archive size={14} aria-hidden />
            )}
            {archive.isPending ? "Archiving…" : "Archive"}
          </Button>
        </>
      }
    >
      <p className="text-small text-onyx mb-space-3">
        <strong>{document.title}</strong>
        {document.folderName ? (
          <span className="text-gray-mid"> · {document.folderName}</span>
        ) : null}
      </p>
      <FormField
        label="Reason for archiving (optional)"
        htmlFor="archive-reason"
      >
        <Textarea
          id="archive-reason"
          rows={3}
          maxLength={REASON_MAX}
          value={reason}
          disabled={archive.isPending}
          placeholder="e.g. Superseded by 2026 brand guide"
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
        />
      </FormField>
      <div className="text-caption text-gray-mid mt-space-1">
        {reason.length}/{REASON_MAX}
      </div>
    </Modal>
  );
}
